import {observable, computed, action} from "mobx";
import BaseTableModel from "./BaseTableModel";
import TableApiStorageProvider from "../storage/TableApiStorageProvider";
import LoadingStates from "./LoadingStates";
import range from "lodash/range";
import map from "lodash/map";
import mapValues from "lodash/mapValues";
import filter from "lodash/filter";
import includes from "lodash/includes";
import forEach from "lodash/forEach";
import isEmpty from "lodash/isEmpty";
import extend from "lodash/extend";
import pickBy from "lodash/pickBy";
import reverse from "lodash/reverse";
import queryString from "query-string";

class DynamicTableModel extends BaseTableModel {
    @observable cacheData = observable.map({});
    @observable requestId = 0;
    @observable recordCount = 0;
    @observable loadState;
    @observable selectedRows = observable.set();
    @observable selectedRowData = observable.map({});

    constructor(props) {
        super(props);
        this.loadState = props.loadState || LoadingStates.DATA_REFRESH_NEEDED;
        this.dispatcher = null;
        this.baseEndpoint = props.endpoint || "";
        this.storageProvider = new TableApiStorageProvider(this.baseEndpoint);
        this.selectionKey = props.selectionKey || null;
        this.selectableFilter = props.selectableFilter || null;
        this.preserveSelectionOnPageChange = props.preserveSelectionOnPageChange || false;
    }

    @computed
    get allSelected() {
        if (!this.selectionKey || !this.currentData || !this.currentData.filter) {
            return false;
        }
        const selectableRows = this.currentData.filter((row) => {
            if (!this.selectableFilter) {
                return true;
            }
            return this.selectableFilter(row.rowData);
        });
        if (selectableRows.length === 0) {
            return false;
        }
        return selectableRows.every((row) => this.selectedRows.has(row.rowData.get(this.selectionKey)));
    }

    @action
    toggleRowSelection(keyValue, rowData = null) {
        if (!this.selectionKey) {
            return;
        }
        if (this.selectedRows.has(keyValue)) {
            this.selectedRows.delete(keyValue);
            this.selectedRowData.delete(keyValue);
        } else {
            this.selectedRows.add(keyValue);
            if (rowData) {
                this.selectedRowData.set(keyValue, rowData);
            }
        }
    }

    @action
    selectAll(checked) {
        if (!this.selectionKey || !this.currentData || !this.currentData.filter) {
            return;
        }
        const selectableRows = this.currentData.filter((row) => {
            if (!this.selectableFilter) {
                return true;
            }
            return this.selectableFilter(row.rowData);
        });
        if (checked) {
            selectableRows.forEach((row) => {
                const keyValue = row.rowData.get(this.selectionKey);
                this.selectedRows.add(keyValue);
                this.selectedRowData.set(keyValue, row.rowData);
            });
        } else {
            selectableRows.forEach((row) => {
                const keyValue = row.rowData.get(this.selectionKey);
                this.selectedRows.delete(keyValue);
                this.selectedRowData.delete(keyValue);
            });
        }
    }

    @action
    clearSelection() {
        this.selectedRows.clear();
        this.selectedRowData.clear();
    }

    @computed
    get currentData() {
        const cacheKey = this.generatedCacheKey;
        if (!this.existsInCache(cacheKey, this.currentPage)) {
            return [];
        }
        return this.cacheData.get(cacheKey)[this.currentPage];
    }

    @computed
    get currentFilterState() {
        let filterState = {
            pageSize: this.itemsPerPage
        };

        // filters is a Mobx object
        this.filters.forEach((filterDefinition, filterKey) => { // eslint-disable-line lodash/prefer-lodash-method
            if (filterDefinition.value) {
                filterState[filterKey] = filterDefinition.value;
            }
        });

        const sortDefinitions = reverse(this.sorts.slice());
        forEach(sortDefinitions, (sortDefinition, index) => {
            const columnKey = `sort[${index}].iteratee`;
            const orderKey = `sort[${index}].order`;
            filterState[columnKey] = sortDefinition.iteratee;
            filterState[orderKey] = sortDefinition.descending ? "desc" : "asc";
        });

        return filterState;
    }

    @computed
    get generatedCacheKey() {
        return queryString.stringify(this.currentFilterState);
    }

    @computed
    get currentEndpoint() {
        if (isEmpty(this.baseEndpoint)) {
            return "";
        }

        let queryParams = this.currentFilterState;

        let pages = range(Math.max(0, this.currentPage - 2), this.currentPage + 3);
        const currentCache = this.cacheData.get(this.generatedCacheKey);
        if (currentCache) {
            pages = filter(pages, pageIndex => !currentCache[pageIndex]);
        }
        queryParams.pages = pages;
        queryParams.requestId = this.requestId;

        return this.baseEndpoint + (includes(this.baseEndpoint, "?") ? "&" : "?") + queryString.stringify(queryParams);
    }

    @computed
    get pageCount() {
        return Math.ceil(this.recordCount / this.itemsPerPage);
    }

    @computed
    get isLoaded() {
        return this.loadState === LoadingStates.DATA_LOADED;
    }

    @action
    changePage(newPage) {
        this.currentPage = newPage;
        if (!this.preserveSelectionOnPageChange) {
            this.clearSelection();
        }
        this.refreshTable();
    }

    @action
    refreshTable() {
        if (this.existsInCache(this.generatedCacheKey, this.currentPage)) {
            const endpointSpecificCache = this.cacheData.get(this.generatedCacheKey);
            this.recordCount = endpointSpecificCache.recordCount;
        } else {
            this.retrievePagesFromServer();
        }
    }

    @action
    clearCache() {
        this.cacheData.clear();
        this.clearSelection();
        this.refreshTable();
    }

    @action
    updateItemsPerPage(newPageSize) {
        this.clearSelection();
        super.updateItemsPerPage(newPageSize);
        this.refreshTable();
    }

    @action
    updateSort(columnName) {
        this.clearSelection();
        super.updateSort(columnName);
        this.refreshTable();
    }

    @action
    updateFilter(filterKey, newValue) {
        const previousCacheKey = this.generatedCacheKey;
        super.updateFilter(filterKey, newValue);
        if (this.generatedCacheKey !== previousCacheKey) {
            this.clearSelection();
        }
        if (!this.filters.get(filterKey).isNoOp) {
            this.refreshTable();
        }
    }

    @action
    insertIntoCache(cacheKey, pagesToBeInserted) {
        const pageData = extend({}, this.cacheData.get(cacheKey), pagesToBeInserted);
        let tempCacheToBeMerged = {};
        tempCacheToBeMerged[cacheKey] = pageData;
        this.cacheData.merge(tempCacheToBeMerged);
    }

    existsInCache(cacheKey, page) {
        if (this.cacheData.get(cacheKey) !== undefined) {
            const cache = this.cacheData.get(cacheKey);
            if (cache.recordCount === 0) { // must explicitly check for an empty record set as it will have no pages and Boolean(cache[page]) would return false.
                return true;
            }
            return Boolean(cache[page]);
        }
        return false;
    }

    @action
    retrievePagesFromServer() {
        // We don't do the retrieval right away in case the View is not ready (i.e. elements are not mounted in the DOM yet),
        // instead, we set a flag for the View to see, so it knows to trigger the retrieval when it's ready
        if (this.loadState === LoadingStates.UI_NOT_READY || this.loadState === LoadingStates.DATA_REFRESH_NEEDED) {
            this.loadState = LoadingStates.DATA_REFRESH_NEEDED;
            return;
        }

        // Force handle one search request at a time, GR-6954
        if (this.loadState === LoadingStates.DATA_LOADING) {
            return;
        }

        // If you get here, the View is ready (and potentially called this method), so it's safe to do the retrieval
        this.loadState = LoadingStates.DATA_LOADING;
        this.requestId++;
        this.storageProvider.endpoint = this.currentEndpoint;
        this.storageProvider.get(this.dispatcher, action((res) => {
            if (this.requestId > res.body.requestId) {
                return;
            }

            this.sorts = reverse(map(res.body.sort, (sortDefinition, index) => {
                return {
                    iteratee: sortDefinition.iteratee,
                    descending: sortDefinition.order === "desc",
                    primary: index === 0
                };
            }));

            let pagesToInclude = pickBy(res.body.results, pageEntries => !isEmpty(pageEntries));
            pagesToInclude = mapValues(pagesToInclude, (pageContents, pageNumber) => map(pageContents, row => this.createRowModel(row)));
            pagesToInclude.recordCount = res.body.recordCount;
            this.recordCount = res.body.recordCount;
            this.insertIntoCache(this.generatedCacheKey, pagesToInclude);

            this.loadState = LoadingStates.DATA_LOADED;
        }));
    }
}

export default DynamicTableModel;
